import * as Cl from '@design-system/component-library'; // NOSONAR
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { CallerDisplayNameListFields } from '../../common/enums';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import { cancelMsg, editMsg, numberMsg, saveMsg, subscriptionTypeMsg, t } from '../../common/i18n/index.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { ConfirmationDialog } from '../ConfirmationDialog/index.js';
import { ConsolidatedListView } from '../ConsolidatedListView/ConsolidatedListView.js';
import {
  DEFAULT_ITEMS_PER_PAGE,
  type DefaultListSearchParams,
  ListPagination,
  ListSearch,
  SearchFilters,
  Table,
} from '../Table/index.js';
import { setDefaultItemsPerPage } from '../Table/tableUtils.js';
import { getCallerDisplaySubscriptionTypeFilterGroup } from './CallerDisplayHooks/callerDisplayFilters.js';
import { useCallerDisplayNameSetting } from './CallerDisplayHooks/useCallerDisplayNameSetting.js';
import {
  formatCallerDisplayNameRowBulkEdit,
  formatCallerDisplayNameRowEdit,
} from './CallerDisplayNameSettingRow/CallerDisplayNameSettingRowEdit.js';
import { formatCallerDisplayNameRowView } from './CallerDisplayNameSettingRow/CallerDisplayNameSettingRowView.js';
import type { CLTableColumn } from '../../common/types/componentLibrary';

export const CallerDisplayNameSetting = () => {
  const callerDisplayTableColumns: CLTableColumn[] = [
    {
      key: CallerDisplayNameListFields.SUBSCRIPTION_LINK,
      label: `${t.QH8P(subscriptionTypeMsg)} *`,
      sortable: true,
    },
    {
      key: CallerDisplayNameListFields.PHONE_NUMBER,
      label: `${t.CCB1(numberMsg)} *`,
      sortable: true,
    },
    {
      key: CallerDisplayNameListFields.ACTIVE_CALL_DISPLAY,
      label: t.E6CG('Active call display info'),
      sortable: false,
    },
    {
      key: CallerDisplayNameListFields.DIRECTORY_INFO,
      label: t.NM4G('Directory info'),
      sortable: false,
    },
    {
      key: CallerDisplayNameListFields.CALLER_DISPLAY_NAME,
      label: t.ORKT('Caller display name'),
      sortable: false,
    },
    {
      key: CallerDisplayNameListFields.COMPANY_DISPLAY_NAME,
      label: t.WP25('Company display name'),
      sortable: false,
    },
    {
      key: 'actionButtons',
      label: '',
      sortable: false,
    },
  ];
  const { authenticatedUser } = useAuth();
  const searchParams = useSearchParams<DefaultListSearchParams & { subscriptionType?: string }>();
  const { subscriptionType } = searchParams;
  const [filtersOpen, setFiltersOpen] = useState(false);
  const searchFilterGroups = useMemo(
    () => [getCallerDisplaySubscriptionTypeFilterGroup(subscriptionType)],
    [subscriptionType]
  );
  const {
    rows,
    isLoading,
    totalItems,
    searchQuery,
    editingValues,
    isBulkEdit,
    confirmResetRow,
    setConfirmResetRow,
    editCallerDisplayRow,
    cancelEdit,
    startTableEditing,
    submitEdit,
    setEditingValue,
    resetCallerDisplayRow,
    confirmResetCallerDisplayRow,
  } = useCallerDisplayNameSetting();

  const tableRows = rows.map(row => {
    if (isBulkEdit) {
      const values = editingValues[row.id] ?? { user: row.displayUser, owner: row.displayCompany };
      return formatCallerDisplayNameRowBulkEdit(
        row,
        values,
        user => setEditingValue(row.id, 'user', user),
        owner => setEditingValue(row.id, 'owner', owner),
        corporateUser => setEditingValue(row.id, 'corporateUser', corporateUser),
        corporateOwner => setEditingValue(row.id, 'corporateOwner', corporateOwner),
        searchQuery
      );
    }
    if (row.id in editingValues) {
      const values = editingValues[row.id];
      return formatCallerDisplayNameRowEdit(
        row,
        {
          displayUser: values.user,
          displayCompany: values.owner,
          corporateDisplayUser: values.corporateUser,
          corporateDisplayCompany: values.corporateOwner,
        },
        {
          onUserChange: user => setEditingValue(row.id, 'user', user),
          onOwnerChange: owner => setEditingValue(row.id, 'owner', owner),
          onCorporateUserChange: corporateUser => setEditingValue(row.id, 'corporateUser', corporateUser),
          onCorporateOwnerChange: corporateOwner => setEditingValue(row.id, 'corporateOwner', corporateOwner),
          onCancelEdit: cancelEdit,
          onSubmitEdit: submitEdit,
        },
        searchQuery
      );
    }
    return formatCallerDisplayNameRowView(row, editCallerDisplayRow, resetCallerDisplayRow, searchQuery);
  });

  return (
    <>
      {confirmResetRow && (
        <ConfirmationDialog
          header={t.BH8T('Restore original information?')}
          body={
            <>
              {t.BXJC(
                'The information shown in the service will be reset to the directory information provided by the directory service. You can update the directory information and its visibility in the '
              )}
              <Link to={confirmResetRow.subscriptionLink}>{t.BXJD('subscription details')}</Link>.
            </>
          }
          confirmButtonText={t.NG7X('Restore')}
          onCloseDialog={() => setConfirmResetRow(undefined)}
          onConfirm={confirmResetCallerDisplayRow}
        />
      )}
      <div>
        <p>
          {t.D1HY(
            'Manage the caller information shown to call recipients for your company’s subscriptions. You can edit the information or restore it to match the directory information.'
          )}
        </p>
        <p>{t.JM9A('Select the contacts whose caller display names you want to edit.')}</p>
      </div>
      <div className={`${dsClass.MARGIN_TOP_4} ${dsClass.PADDING_BOTTOM_6}`}>
        <ConsolidatedListView
          authenticatedUser={authenticatedUser}
          listElement={
            <Table
              columns={callerDisplayTableColumns}
              isLoading={isLoading}
              rows={tableRows}
              noItemsText={isLoading ? undefined : t.OQBT('No results')}
            />
          }
          listSearch={
            <div
              className={`${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}
              style={{ flexWrap: 'wrap', columnGap: 'var(--eds-spacing-4)' }}
            >
              <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                <ListSearch
                  filtersOpen={filtersOpen}
                  setFiltersOpen={setFiltersOpen}
                  tooltipText={t.RHTI('The search works only in the order type and number columns')}
                  onSearch={() => setDefaultItemsPerPage(new URLSearchParams(searchParams), DEFAULT_ITEMS_PER_PAGE)}
                />
              </div>
              {isBulkEdit ? (
                <div
                  className={`${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER}`}
                  style={{ flexWrap: 'wrap', gap: 'var(--eds-spacing-2)' }}
                >
                  <Cl.Button onClick={submitEdit}>{t.NIWE(saveMsg)}</Cl.Button>
                  <Cl.Button color="link" onClick={cancelEdit}>
                    {t.B2V1(cancelMsg)}
                  </Cl.Button>
                </div>
              ) : (
                <div>
                  <Cl.Button color="light" onClick={startTableEditing}>
                    {t.NVPK(editMsg)}
                  </Cl.Button>
                </div>
              )}
            </div>
          }
          searchFilters={
            <SearchFilters
              displayActiveFiltersAsTiles={!filtersOpen}
              filterGroups={searchFilterGroups}
              onModalClose={() => setFiltersOpen(false)}
            />
          }
          listPagination={<ListPagination itemsPerPageOptions={[30, 60, 100]} totalItems={totalItems} />}
        />
      </div>
    </>
  );
};
